/**
 * FailureStep - Step 3: Define failure mode and root cause
 */

import React, { useState } from 'react';
import type { ScratchFormData } from '../ScratchDetailsScreen';
import { useTerminology } from '@/hooks/useTerminology';

interface FailureStepProps {
  data: ScratchFormData;
  onChange: (updates: Partial<ScratchFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const CATEGORIES_5M = [
  { value: 'MAN', label: 'Man', description: 'Human error, training, procedures' },
  { value: 'MACHINE', label: 'Machine', description: 'Equipment, hardware, tools' },
  { value: 'METHOD', label: 'Method', description: 'Process, workflow, standards' },
  { value: 'MATERIAL', label: 'Material', description: 'Raw materials, supplies, parts' },
  { value: 'MEASUREMENT', label: 'Measurement', description: 'Sensors, calibration, data' },
  { value: 'ENVIRONMENT', label: 'Environment', description: 'Temperature, humidity, conditions' },
] as const;

export function FailureStep({ data, onChange, onNext, onBack }: FailureStepProps) {
  const { t } = useTerminology();
  const [errors, setErrors] = useState<{
    description?: string;
    cause?: string;
  }>({});

  const handleChange = (field: keyof typeof data.failure, value: string) => {
    onChange({
      failure: {
        ...data.failure,
        [field]: value,
      },
    });

    if (field in errors && errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleNext = () => {
    const newErrors: { description?: string; cause?: string } = {};

    if (!data.failure.description || data.failure.description.trim().length < 5) {
      newErrors.description = 'Failure description must be at least 5 characters';
    }

    if (!data.failure.cause || data.failure.cause.trim().length < 5) {
      newErrors.cause = 'Failure cause must be at least 5 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onNext();
  };

  const isValid =
    data.failure.description.trim().length >= 5 && data.failure.cause.trim().length >= 5;

  return (
    <div className="step-container" data-testid="failure-step">
      <div>
        <h2 className="step-title">Identify a potential failure</h2>
        <p className="step-subtitle">
          What could go wrong with this component? Describe a failure mode and its potential cause.
        </p>
      </div>

      <div className="step-form">
        {/* Failure Description */}
        <div className="form-field">
          <label htmlFor="failure-description">
            Failure Description <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            id="failure-description"
            type="text"
            value={data.failure.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="e.g., Motor overheats, Sensor gives incorrect reading"
            data-testid="failure-description-input"
          />
          {errors.description && <span className="field-error">{errors.description}</span>}
          <span className="field-hint">What could fail? Be specific about the failure mode.</span>
        </div>

        {/* Potential Cause */}
        <div className="form-field">
          <label htmlFor="failure-cause">
            Potential Cause <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            id="failure-cause"
            type="text"
            value={data.failure.cause}
            onChange={(e) => handleChange('cause', e.target.value)}
            placeholder="e.g., Excessive load, Calibration drift, Power surge"
            data-testid="failure-cause-input"
          />
          {errors.cause && <span className="field-error">{errors.cause}</span>}
          <span className="field-hint">What would cause this failure to occur?</span>
        </div>

        {/* 5M Category */}
        <div className="form-field">
          <label htmlFor="failure-category">{t.rootCause} Category (5M)</label>
          <select
            id="failure-category"
            value={data.failure.category}
            onChange={(e) =>
              handleChange('category', e.target.value as ScratchFormData['failure']['category'])
            }
            data-testid="failure-category-select"
          >
            {CATEGORIES_5M.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label} - {cat.description}
              </option>
            ))}
          </select>
          <span className="field-hint">Categorize the root cause using the 5M framework</span>
        </div>
      </div>

      <div className="step-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onBack}
          data-testid="back-button"
        >
          Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNext}
          disabled={!isValid}
          data-testid="next-button"
        >
          Next: Assess Risk
        </button>
      </div>
    </div>
  );
}
